/** A quoted-price allocation, not an independent estimate of job costs. */
export interface QuotedCostLine {
  name?: string | null;
  quantity: string | number;
  unitCost: string | number;
  subtotal?: string | number | null;
  labourCost?: string | number | null;
  materialCost?: string | number | null;
  plantCost?: string | number | null;
}

const positive = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const cents = (value: unknown) => Math.round(positive(value) * 100);
export const quotedMoney = (value: number) => Math.round(value * 100) / 100;

export function fitQuotedCostSplit(
  subtotal: number,
  costs: { materialCost: number; plantCost: number },
  name = "",
) {
  const target = cents(subtotal);
  let material = cents(costs.materialCost);
  let plant = cents(costs.plantCost);
  const overBudget = material + plant > target;
  if (name.toLowerCase().includes("supply only")) {
    material = target;
    plant = 0;
  } else if (/\b(?:labour|labor) only\b/i.test(name)) {
    material = 0;
    plant = 0;
  } else if (overBudget) {
    // Fit in integer pennies so rounding cannot create a negative remainder.
    material = Math.round(target * material / (material + plant));
    plant = target - material;
  }
  return {
    labourCost: (target - material - plant) / 100,
    materialCost: material / 100,
    plantCost: plant / 100,
    overBudget,
  };
}

export function getQuotedLineSplit(item: QuotedCostLine) {
  return fitQuotedCostSplit(
    Number(item.subtotal ?? positive(item.quantity) * positive(item.unitCost)),
    { materialCost: positive(item.materialCost), plantCost: positive(item.plantCost) },
    item.name ?? "",
  );
}

/** Deliberate price/quantity edits preserve the reconciled item's proportions. */
export function getEditedQuotedLineSplit(item: QuotedCostLine, original?: QuotedCostLine) {
  const total = positive(item.quantity) * positive(item.unitCost);
  const oldTotal = original && positive(original.quantity) * positive(original.unitCost);
  const unchangedComponents = original && (["labourCost", "materialCost", "plantCost"] as const)
    .every(key => Number(item[key] ?? 0) === Number(original[key] ?? 0));
  if (oldTotal && quotedMoney(oldTotal) > 0 && unchangedComponents && total !== oldTotal) {
    const prior = getQuotedLineSplit(original!);
    const factor = quotedMoney(total) / quotedMoney(oldTotal);
    return fitQuotedCostSplit(total, {
      materialCost: prior.materialCost * factor,
      plantCost: prior.plantCost * factor,
    }, item.name ?? "");
  }
  return getQuotedLineSplit({ ...item, subtotal: total });
}

/** Pure projection for legacy reads and exports; never mutates saved rows. */
export function withQuotedCostSplit<T extends QuotedCostLine>(item: T) {
  const split = getQuotedLineSplit(item);
  return {
    ...item,
    labourCost: String(split.labourCost),
    materialCost: String(split.materialCost),
    plantCost: String(split.plantCost),
  };
}
